import { Injectable, inject } from '@angular/core';

import { type Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import type { EventToken } from '@core/decorators/class-name.decorator';

import type { BaseEvent, EventTypeMap } from '@models/events.model';

import { SignalRService } from './signalr.service';

/**
 * Incoming SignalR message envelope sent by SignalRFunctions.BroadcastEvent.
 * The server emits `{ eventType, payload }` (eventType = the C# event class name).
 * We also accept a `$type` alias for forward-compatibility.
 */
interface SignalREnvelope {
  eventType?: string;
  $type?: string;
  payload: unknown;
}

/**
 * Generic typed event bus backed by Azure SignalR Service. Ported from
 * Chroniq/Mobile-Client's event-bus.service.ts — domain-agnostic, no Capacitor concerns,
 * so this ports unchanged.
 *
 * This service knows nothing about concrete event shapes. All routing is driven by
 * @className-decorated token classes whose static `$type` literal matches the C# class
 * name sent in the envelope.
 *
 * ── Usage ────────────────────────────────────────────────────────────────────
 *
 * Single event:
 *   eventBus.on(EmployeeUpdatedEventToken).subscribe(e => { ... });
 *
 * Multiple events merged into one stream:
 *   eventBus.on(AbsenceApprovedEventToken, AbsenceRejectedEventToken).subscribe(e => { ... });
 *
 * Raw unfiltered stream (all events as BaseEvent):
 *   eventBus.all$.subscribe(e => console.log(e.$type));
 * ─────────────────────────────────────────────────────────────────────────────
 */
@Injectable({ providedIn: 'root' })
export class EventBusService {
  private readonly signalR = inject(SignalRService);

  private readonly _events$ = new Subject<BaseEvent>();

  /** Raw stream of every incoming event, unfiltered. */
  readonly all$: Observable<BaseEvent> = this._events$.asObservable();

  constructor() {
    this.signalR.registerMessageHandler((data) => this.dispatch(data));
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Returns a typed Observable filtered to the event(s) identified by the
   * given class token(s). TypeScript resolves the concrete event type via
   * EventTypeMap[K] — no explicit type parameter needed at the call site.
   *
   * Single:   on(EmployeeUpdatedEventToken)
   * Multiple: on(AbsenceApprovedEventToken, AbsenceRejectedEventToken)
   */
  on<K extends keyof EventTypeMap>(
    ...tokens: [EventToken<K>, ...EventToken<K>[]]
  ): Observable<EventTypeMap[K]> {
    const types: Set<string> = new Set(tokens.map((t) => t.$type));
    return this._events$.pipe(
      filter((e): e is EventTypeMap[K] => types.has(e.$type)),
    );
  }

  // ─── Internal dispatch ────────────────────────────────────────────────────────

  private dispatch(data: unknown): void {
    if (!data || typeof data !== 'object') {
      return;
    }

    const envelope = data as Partial<SignalREnvelope>;
    // Server sends `eventType`; accept `$type` too. Without this the field never matched
    // and every real-time event was silently dropped.
    const type = envelope.eventType ?? envelope.$type;
    if (!type || envelope.payload == null) {
      return;
    }

    // Reassemble: spread payload fields, then stamp $type so consumers can narrow
    const event = {
      ...(typeof envelope.payload === 'object' ? envelope.payload : {}),
      $type: type,
    } as BaseEvent;

    this._events$.next(event);
  }
}
